import '@/assets/style/companyList.scss';
import { useTransactionContext } from '@/context/TransactionContext';
import CreateButton from '@/utils/components/CreateButton';
import CreateModalButton from '@/utils/components/CreateModelButton';
import OpeningStockUploadButton from '@/utils/components/OpeningStockUploadButton';
import SearchBox from '@/utils/components/SearchBox';
import { Checkbox, Col, Dropdown, Form, Menu, Row } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import SalesUploadButton from '@/utils/components/SalesUploadButton';
import PurchaseUploadButton from '@/utils/components/PurchaseUploadButton';
import useAppData from '@/hooks/useAppData';
import ExcelButton from '@/utils/pdf/Excel';
import printIcon from '@/assets/images/print.svg';
import pdf from '@/assets/images/pdf.svg';
import { generatePDF } from '@/utils/pdf/Pdf';
import { DownOutlined } from '@ant-design/icons';
import { AntButton } from '@/common/ant-components/AntButton';
import DateChangeWithPopOverEnglish from '@/common/custom-date-selector-list/pop-over-english-date';
import DateChangeWithPopOverNepali from '@/common/custom-date-selector-list/pop-over-nepali-date';
import { pathnameWiseName } from './pathnameWiseName';

const ListHeaderComponents = ({
  handleSearch,
  triggerSearch,
  createPath,
  createButtonTitle,
  startDate,
  endDate,
  data,
  columns,
  checkList,
  setCheckList,
  title,
  filename,
  showModal,
  openModal,
  fetchPartyList,
  extraActions,
  hideExport = false,
  refreshOnDateChange = true,
}) => {
  const [form] = Form.useForm();
  const { pathname } = useLocation();
  const [menuVisible, setMenuVisible] = useState();
  const [options, setOptions] = useState([]);

  const {
    dateTypeList,
    setDateTypeList,
    periodValueList,
    persistedDateType,
    setPersistedDateType,
    setPeriodValueList,
    salesStartDate,
    salesEndDate,
    setSalesStartDate,
    setSalesEndDate,
    selectedPeriodList,
    setSelectedPeriodList,
  } = useTransactionContext();

  const isFromStock = useMemo(() => {
    return pathname.includes('stock');
  }, [pathname]);

  const onChange = (e) => {
    setDateTypeList(e?.target?.value);
    setPersistedDateType(e?.target?.value);
  };

  const isSalesUpload = pathname.includes('/sales/list/');
  const isPurchaseUpload = pathname.includes('/purchase/list/');

  const { app } = useAppData();

  const handleGeneratePDF = (type = '', extension = { reportTitle }) => {
    const newColumn = columns?.filter(
      (item) => item.key !== 'action' && checkList?.includes(item.key),
    );

    const tableDataToSend = data?.map((item, index) => ({
      key: item?.referenceId,
      ...item,
    }));

    generatePDF(
      tableDataToSend,
      newColumn,
      type,
      extension,
      startDate,
      endDate,
    );
  };

  useEffect(() => {
    // Assume columns are available (options)
    const newOptions = columns?.map((column) => ({
      label: column.title,
      value: column.key,
    }));
    setOptions(newOptions);
    setCheckList && setCheckList(newOptions?.map((option) => option.value));
  }, [columns]); // Run when columns change

  const handleToggleCheckAll = () => {
    if (checkList?.length === options?.length) {
      setCheckList && setCheckList([]);
    } else {
      setCheckList && setCheckList(options?.map((option) => option.value));
    }
  };

  const handleCheckboxChange = (value) => {
    setCheckList &&
      setCheckList(
        (prevCheckList) =>
          prevCheckList.includes(value)
            ? prevCheckList.filter((item) => item !== value) // Uncheck
            : [...prevCheckList, value], // Check
      );
  };

  const menu = useCallback(
    () => (
      <Menu style={{ maxHeight: '300px', overflowY: 'auto' }}>
        <Menu.Item key="toggleCheckAll" disabled>
          <AntButton
            type="link"
            onClick={handleToggleCheckAll}
            className="tw-w-full"
          >
            {checkList?.length === options?.length
              ? 'Unselect All'
              : 'Select All'}
          </AntButton>
        </Menu.Item>
        {options?.map((option) => (
          <Menu.Item key={option?.value} disabled>
            <Checkbox
              checked={checkList?.includes(option?.value)}
              className="tw-w-full"
              onChange={() => handleCheckboxChange(option?.value)}
            >
              {option?.label}
            </Checkbox>
          </Menu.Item>
        ))}
      </Menu>
    ),
    [options, checkList],
  );

  const handleMenuVisibleChange = (visible) => {
    if (visible) {
      setMenuVisible(true); // Show the menu
    } else {
      setMenuVisible(false); // Hide the menu when clicked outside
    }
  };

  useEffect(() => {
    if (refreshOnDateChange) triggerSearch();
  }, [salesEndDate, salesStartDate]);

  const normalizePath = (path) => {
    return path.endsWith('/') ? path : path + '/';
  };

  return (
    <Form form={form}>
      <Row
        align="middle"
        wrap={true}
        gutter={[16, 16]}
        style={{
          paddingTop: '7px',
          paddingLeft: '15px',
          paddingRight: '15px',
        }}
      >
        <Col xs={24} sm={12} md={10}>
          <div className="tw-text-primary tw-font-semibold tw-text-lg">
            {pathnameWiseName?.[normalizePath(pathname)] || 'List'}
          </div>
        </Col>
      </Row>
      <Row
        align="middle"
        wrap={true}
        gutter={[16, 16]}
        style={{
          paddingTop: '7px',
          paddingLeft: '15px',
          paddingRight: '15px',
        }}
      >
        {/* Search Box */}
        <Col
          xs={24}
          sm={12}
          md={8}
          style={{
            marginRight: app?.dateType === 'ad' ? 'auto' : 0,
          }}
        >
          <SearchBox
            searchPlaceholder="Search list..."
            handleSearch={handleSearch}
          />
        </Col>
        <Col>
          {persistedDateType === 'ad' ? (
            !isFromStock && (
              <DateChangeWithPopOverEnglish
                dateType={persistedDateType}
                radioButtonProps={{
                  onChange: onChange,
                  initialValue: dateTypeList,
                  name: 'date_type',
                }}
                customDateSelectorPropsEnglish={{
                  periodValueList: periodValueList,
                  setPeriodValueList: setPeriodValueList,
                  dateType: persistedDateType,
                  setSalesStartDate: setSalesStartDate,
                  setSalesEndDate: setSalesEndDate,
                  selectedPeriodList: selectedPeriodList,
                  setSelectedPeriodList: setSelectedPeriodList,
                }}
              />
              // <SelectPeriodEnglish
              //   periodValueList={periodValueList}
              //   setPeriodValueList={setPeriodValueList}
              //   dateType={persistedDateType}
              //   colWidth={3}
              //   setSalesStartDate={setSalesStartDate}
              //   setSalesEndDate={setSalesEndDate}
              // />
            )
          ) : (
            <>
              {!isFromStock && (
                // <SelectPeriodNepali
                //   periodValueList={periodValueList}
                //   setPeriodValueList={setPeriodValueList}
                //   dateType={persistedDateType}
                //   colWidth={3}
                //   setSalesStartDate={setSalesStartDate}
                //   setSalesEndDate={setSalesEndDate}
                // />

                <DateChangeWithPopOverNepali
                  dateType={persistedDateType}
                  radioButtonProps={{
                    onChange: onChange,
                    initialValue: dateTypeList,
                    name: 'date_type',
                  }}
                  form={form}
                  customDateSelectorPropsNepali={{
                    periodValueList: periodValueList,
                    setPeriodValueList: setPeriodValueList,
                    dateType: persistedDateType,
                    colWidth: 3,
                    setSalesStartDate: setSalesStartDate,
                    setSalesEndDate: setSalesEndDate,
                    selectedPeriodList: selectedPeriodList,
                    setSelectedPeriodList: setSelectedPeriodList,
                  }}
                />
              )}
            </>
          )}
        </Col>

        <Col>
          {showModal ? (
            <CreateModalButton
              title={createButtonTitle}
              openModal={openModal}
              fetchData={fetchPartyList}
            />
          ) : (
            <div
              className={`${isFromStock || isSalesUpload || isPurchaseUpload ? 'tw-flex tw-items-center xl:tw-gap-6 md:tw-flex-wrap xl:tw-flex-nowrap' : ''}`}
            >
              {isSalesUpload ? (
                <SalesUploadButton refreshTable={triggerSearch} />
              ) : (
                ''
              )}
              {isPurchaseUpload ? (
                <PurchaseUploadButton refreshTable={triggerSearch} />
              ) : (
                ''
              )}
              {createButtonTitle === 'Create New Opening Stock' ? (
                <OpeningStockUploadButton refreshTable={triggerSearch} />
              ) : (
                ''
              )}
            </div>
          )}
        </Col>

        <Col>
          <Dropdown
            popupRender={menu}
            trigger={['click']}
            open={menuVisible}
            onOpenChange={handleMenuVisibleChange}
          >
            <AntButton>
              Columns <DownOutlined />
            </AntButton>
          </Dropdown>
        </Col>

        <Col hidden={hideExport}>
          <div
            style={{
              display: 'flex',
              gap: '10px',
              alignItems: 'center',
            }}
          >
            <ExcelButton
              data={data?.length ? data : []}
              columns={
                columns
                  ? columns
                      ?.map((item) => item?.key)
                      ?.filter(
                        (item) =>
                          item?.toLowerCase() !== 'action' &&
                          checkList.includes(item),
                      )
                  : []
              }
              startDate={startDate}
              endDate={endDate}
              title={title}
              fileName={filename}
            />
            <AntButton
              type="button"
              onClick={() => handleGeneratePDF('', filename)}
              style={{
                border: 'none',
                background: 'none',
                padding: 0,
              }}
            >
              <img className="table-icon" src={pdf} alt="Download PDF" />
            </AntButton>
            <AntButton
              type="button"
              onClick={() => handleGeneratePDF('print', filename)}
              style={{
                border: 'none',
                background: 'none',
                padding: 0,
              }}
            >
              <img className="table-icon" src={printIcon} alt="Download PDF" />
            </AntButton>
          </div>
        </Col>

        <Col
          xs={24}
          sm={12}
          md={15}
          lg={isFromStock ? 15 : 5}
          xl={5}
          style={{
            marginLeft: 'auto',
          }}
        >
          {showModal ? (
            <CreateModalButton
              title={createButtonTitle}
              openModal={openModal}
              fetchData={fetchPartyList}
            />
          ) : (
            <div className={`tw-flex tw-items-center tw-justify-end tw-gap-2`}>
              {extraActions}
              <CreateButton path={createPath} title="Create" />
              {createButtonTitle === 'Create New Opening Stock' ? (
                <OpeningStockUploadButton refreshTable={triggerSearch} />
              ) : (
                ''
              )}
              {/* {persistedDateType === 'ad' ? (
                !isFromStock && (
                  <DateChangeWithPopOverEnglish
                    dateType={persistedDateType}
                    radioButtonProps={{
                      onChange: onChange,
                      initialValue: dateTypeList,
                      name: 'date_type',
                    }}
                    customDateSelectorPropsEnglish={{
                      periodValueList: periodValueList,
                      setPeriodValueList: setPeriodValueList,
                      dateType: persistedDateType,
                      setSalesStartDate: setSalesStartDate,
                      setSalesEndDate: setSalesEndDate,
                      selectedPeriodList: selectedPeriodList,
                      setSelectedPeriodList: setSelectedPeriodList,
                    }}
                  />
                  // <SelectPeriodEnglish
                  //   periodValueList={periodValueList}
                  //   setPeriodValueList={setPeriodValueList}
                  //   dateType={persistedDateType}
                  //   colWidth={3}
                  //   setSalesStartDate={setSalesStartDate}
                  //   setSalesEndDate={setSalesEndDate}
                  // />
                )
              ) : (
                <>
                  {!isFromStock && (
                    // <SelectPeriodNepali
                    //   periodValueList={periodValueList}
                    //   setPeriodValueList={setPeriodValueList}
                    //   dateType={persistedDateType}
                    //   colWidth={3}
                    //   setSalesStartDate={setSalesStartDate}
                    //   setSalesEndDate={setSalesEndDate}
                    // />

                    <DateChangeWithPopOverNepali
                      dateType={persistedDateType}
                      radioButtonProps={{
                        onChange: onChange,
                        initialValue: dateTypeList,
                        name: 'date_type',
                      }}
                      form={form}
                      customDateSelectorPropsNepali={{
                        periodValueList: periodValueList,
                        setPeriodValueList: setPeriodValueList,
                        dateType: persistedDateType,
                        colWidth: 3,
                        setSalesStartDate: setSalesStartDate,
                        setSalesEndDate: setSalesEndDate,
                        selectedPeriodList: selectedPeriodList,
                        setSelectedPeriodList: setSelectedPeriodList,
                      }}
                    />
                  )}
                </>
              )} */}
            </div>
          )}

          {/* </div> */}
        </Col>

        {/* Date Range Picker */}
        {/* {!isFromStock && (
          <Col
            xs={app?.dateType === 'ad' ? 0 : 24}
            sm={app?.dateType === 'ad' ? 0 : 12}
            lg={app?.dateType === 'ad' ? 0 : 2}
            style={{
              marginLeft: 'auto',
            }}
          >
            <RadioButtonGroup
              name="date_type"
              initialValue={dateTypeList}
              onChange={onChange}
            />
          </Col>
        )} */}

        {/* <Col
          style={{
            marginLeft: 'auto',
          }}
        >
          {persistedDateType === 'ad' ? (
            !isFromStock && (
              <DateChangeWithPopOverEnglish
                dateType={persistedDateType}
                radioButtonProps={{
                  onChange: onChange,
                  initialValue: dateTypeList,
                  name: 'date_type',
                }}
                customDateSelectorPropsEnglish={{
                  periodValueList: periodValueList,
                  setPeriodValueList: setPeriodValueList,
                  dateType: persistedDateType,
                  setSalesStartDate: setSalesStartDate,
                  setSalesEndDate: setSalesEndDate,
                  selectedPeriodList: selectedPeriodList,
                  setSelectedPeriodList: setSelectedPeriodList,
                }}
              />
              // <SelectPeriodEnglish
              //   periodValueList={periodValueList}
              //   setPeriodValueList={setPeriodValueList}
              //   dateType={persistedDateType}
              //   colWidth={3}
              //   setSalesStartDate={setSalesStartDate}
              //   setSalesEndDate={setSalesEndDate}
              // />
            )
          ) : (
            <>
              {!isFromStock && (
                // <SelectPeriodNepali
                //   periodValueList={periodValueList}
                //   setPeriodValueList={setPeriodValueList}
                //   dateType={persistedDateType}
                //   colWidth={3}
                //   setSalesStartDate={setSalesStartDate}
                //   setSalesEndDate={setSalesEndDate}
                // />

                <DateChangeWithPopOverNepali
                  dateType={persistedDateType}
                  radioButtonProps={{
                    onChange: onChange,
                    initialValue: dateTypeList,
                    name: 'date_type',
                  }}
                  form={form}
                  customDateSelectorPropsNepali={{
                    periodValueList: periodValueList,
                    setPeriodValueList: setPeriodValueList,
                    dateType: persistedDateType,
                    colWidth: 3,
                    setSalesStartDate: setSalesStartDate,
                    setSalesEndDate: setSalesEndDate,
                    selectedPeriodList: selectedPeriodList,
                    setSelectedPeriodList: setSelectedPeriodList,
                  }}
                />
              )}
            </>
          )}
        </Col> */}

        {/* <Col xs={24} sm={12} lg={2}>
          <AntButton loading={loading} block onClick={triggerSearch}>
            Search
          </AntButton>
        </Col> */}
      </Row>
    </Form>
  );
};

export default ListHeaderComponents;
